// src/pages/dashboard/directorproducer/ManageRolesPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaEdit, 
  FaPlus, 
  FaClone, 
  FaTrash, 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaLayerGroup,FaFilm,FaUsers,FaCheckCircle,FaTimesCircle,FaClock
} from 'react-icons/fa';

import { fetchRoleListApi, deleteRoleApi, getAppliedRoleListForDrCr } from '../../services/api';
import MetaTags from '../../components/MetaTags'; 
import DataTable from '../../components/DataTable'; 
import './ManageRolesPage.css';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';

const ManageRolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
    const hasPermission = (moduleKey, action = 'View') => {
    if (String(user?.roleCode || user?.role_code) === '900') {
      if (action === 'Delete') return false;
      const perms = user?.permissions || [];
      return perms.includes(`${moduleKey}:${action}`);
    }
    return true;
  };

  const formatCamelCase = (text) => {
    if (!text) return "";
    return text
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const getAuditionLocation = (role, parsedRequestData = {}) => {
  try {
    let locations =
      role.audition_locations_json ||
      parsedRequestData.audition_locations_json ||
      parsedRequestData.auditionLocations ||
      [];

    // JSON string ko parse karo
    if (typeof locations === 'string') {
      locations = JSON.parse(locations);
    }

    if (!Array.isArray(locations) || locations.length === 0) {
      return role.shoot_location || role.roleLocation || 'N/A';
    }

    // ✅ ALL locations + ALL pincodes
    const formattedLocations = locations
      .map((location) => {
        const district = location.district || '';
        const state = location.state || '';
        const pincode = location.pincode || '';

        let locationName = '';

        if (district && state) {
          locationName = `${district}, ${state}`;
        } else if (district) {
          locationName = district;
        } else if (state) {
          locationName = state;
        } else if (location.address1) {
          locationName = location.address1;
        }

        // Location + Pincode
        if (locationName && pincode) {
          return `${locationName} - ${pincode}`;
        }

        return locationName || pincode;
      })
      .filter(Boolean);

    return formattedLocations.length > 0
      ? formattedLocations.join('; ')
      : role.shoot_location || role.roleLocation || 'N/A';

  } catch (error) {
    console.error('Error parsing audition location:', error);

    return role.shoot_location || role.roleLocation || 'N/A';
  }
};
  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0,
    totalRoles: 0,
    totalAuditions: 0,
    selected: 0,
    rejected: 0,
    pending: 0
  });

  // ✅ LOAD & MAP ROLES
  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const res = await fetchRoleListApi();
      const rolesData = res?.data?.data || res?.data || [];
      const appResponse = await getAppliedRoleListForDrCr();
      const applications = appResponse?.data?.data || [];

      const mappedRoles = rolesData.map((role) => {
        let parsedRequestData = {};
        if (role.request_data) {
          try {
            parsedRequestData = typeof role.request_data === 'string' 
              ? JSON.parse(role.request_data) 
              : role.request_data;
          } catch (e) {
            console.error("Error parsing request_data for role", role.id);
          }
        }

        const roleApplications = applications.filter(app => {
          const applicationRoleId = app.c_roleId || app.c_role_id || app.role_joget_id || app.joget_role_id || app.roleId || '';
          const currentRoleId = role.joget_role_id || role.roleId || '';
          return String(applicationRoleId) === String(currentRoleId);
        });

        const selectedCount = roleApplications.filter(
          app => String(app.result || app.c_result || '').toLowerCase() === 'selected'
        ).length;

        const rejectedCount = roleApplications.filter(
          app => String(app.result || app.c_result || '').toLowerCase() === 'rejected'
        ).length;

        const pendingCount = roleApplications.filter(app => {
          const result = String(app.result || app.c_result || '').toLowerCase();
          return (result === 'pending' || result === 'resubmit');
        }).length;

        return {
          ...parsedRequestData,
          id: role.id,
          joget_role_id: role.joget_role_id,
          roleld: role.roleld || parsedRequestData.roleld || '---',
          roleName: role.role_name || role.roleName || parsedRequestData.roleName || 'N/A',
          roleCategory: role.role_category || role.roleCategory || parsedRequestData.roleCategory || 'N/A',
          auditionType: parsedRequestData.auditionType || role.audition_type || role.auditionType || 'N/A',
          // roleLocation: role.shoot_location || role.roleLocation || parsedRequestData.roleLocation || 'N/A',
          roleLocation: getAuditionLocation(role, parsedRequestData),
          projectName: role.projectName || role['project.projectName'] || role.project?.project_name || parsedRequestData.projectName || '---',
          totalApplicants: roleApplications.length,
          selectedCount,
          rejectedCount,
          pendingCount,
          status: role.status || 'N/A',
          dateCreated: role.created_at || role.dateCreated || 0,
        };
      });

      const sortedRoles = mappedRoles.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
      setRoles(sortedRoles);

      const uniqueProjects = new Set(sortedRoles.map(r => r.projectName));
      const totalAuditions = applications.length;

      const selectedCount = applications.filter(item => String(item.result || '').toLowerCase() === 'selected').length;
      const rejectedCount = applications.filter(item => String(item.result || '').toLowerCase() === 'rejected').length;
      const pendingCount = applications.filter(item => {
        const status = String(item.result || '').toLowerCase();
        return (status === 'pending' || status === 'applied' || status === 'resubmit');
      }).length;

      setDashboardStats({
        totalProjects: uniqueProjects.size,
        totalRoles: sortedRoles.length,
        totalAuditions,
        selected: selectedCount,
        rejected: rejectedCount,
        pending: pendingCount
      });
    } catch (err) { 
      console.error(err);
      setError('Failed to load roles.'); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    loadRoles(); 
  }, [loadRoles]);

  // ✅ EDIT ROLE
  const handleEdit = (localId) => {
    navigate(`/dashboard/post-roles/${localId}`);
  };

  // ✅ CLONE ROLE
  const handleClone = (role) => {
    const clonedRole = { ...role };
    delete clonedRole.id;
    delete clonedRole.joget_role_id;
    clonedRole.roleName = `${role.roleName} (Clone)`;

    navigate('/dashboard/post-roles', { state: { roleToClone: clonedRole } });
  };

  // ✅ DELETE ROLE (With Compact SweetAlert2 & Applicants Check)
  const handleDelete = async (roleOrId) => {
    let role = null;
    let localId = null;

    if (typeof roleOrId === 'object' && roleOrId !== null) {
      role = roleOrId;
      localId = roleOrId.id;
    } else {
      localId = roleOrId;
      role = roles.find(r => String(r.id) === String(localId));
    }

    if (!localId || !role) return;

    const applicantsCount = role.totalApplicants || 0;

    // 1. Agar Applicants hain to Delete BLOCK kar do
    if (applicantsCount > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Delete Role!',
        html: `This role currently has <b style="color: #38bdf8">${applicantsCount} Applicant(s)</b>.<br/><br/>You cannot delete a role that has active applicants.`,
        background: '#0E1124',
        color: '#fff',
        confirmButtonColor: '#d90429',
        width: '380px', // 🟢 Compact Width
        customClass: {
          popup: 'compact-swal-popup',
          title: 'compact-swal-title',
          htmlContainer: 'compact-swal-text'
        }
      });
      return;
    }

    // 2. Compact Confirm SweetAlert
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you really want to delete role "${role.roleName || ''}"?`,
      icon: 'warning',
      showCancelButton: true,
      background: '#0E1124',
      color: '#fff',
      confirmButtonColor: '#d90429',
      cancelButtonColor: '#1E2342',
      confirmButtonText: 'Yes, Delete it!',
      width: '380px', // 🟢 Compact Width
      customClass: {
        popup: 'compact-swal-popup',
        title: 'compact-swal-title',
        htmlContainer: 'compact-swal-text'
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteRoleApi(localId);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Role deleted successfully!',
          background: '#0E1124',
          color: '#fff',
          confirmButtonColor: '#10B981',
          width: '380px',
          customClass: {
            popup: 'compact-swal-popup',
            title: 'compact-swal-title',
            htmlContainer: 'compact-swal-text'
          }
        });
        loadRoles();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed!',
          text: err.response?.data?.message || 'Failed to delete role.',
          background: '#0E1124',
          color: '#fff',
          confirmButtonColor: '#d90429',
          width: '380px',
          customClass: {
            popup: 'compact-swal-popup',
            title: 'compact-swal-title',
            htmlContainer: 'compact-swal-text'
          }
        });
      }
    }
  };

  // ✅ COLUMNS FOR DATATABLE
  const columns = useMemo(() => [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => <span className="opacity-50 text-white">{info.row.index + 1}</span>,
      enableSorting: false,
    },
    {
      accessorKey: 'roleName',
      header: 'Role Title',
      cell: (info) => <span className="fw-bold text-white">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectName',
      header: 'Project',
      cell: (info) => <span className="text-warning fw-bold">{info.getValue()}</span>,
    },
    {
      accessorKey: 'roleCategory',
      header: 'Category',
      cell: (info) => <span className="text-capitalize">{formatCamelCase(info.getValue())}</span>,
    },
    {
      accessorKey: 'auditionType',
      header: 'Mode',
      cell: (info) => <span className="small">{formatCamelCase(info.getValue())}</span>,
    },
    {
      accessorKey: 'roleLocation',
      header: 'Location',
    },
    {
      accessorKey: 'totalApplicants',
      header: 'Applicants',
      cell: (info) => <span className="text-warning fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'pendingCount',
      header: 'Pending',
      cell: (info) => <span className="text-primary fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'selectedCount',
      header: 'Selected',
      cell: (info) => <span className="text-success fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'rejectedCount',
      header: 'Rejected',
      cell: (info) => <span className="text-danger fw-bold">{info.getValue() || 0}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const val = info.getValue();
        const isInactive = String(val).toLowerCase() === 'inactive';
        return (
          <span className={`mrp-status-tag ${isInactive ? 'mrp-status-inactive' : 'mrp-status-active'}`}>
            {val}
          </span>
        );
      },
    },
        {
      id: 'actions',
      header: () => <div style={{ textAlign: "center", width: "100%" }}>ACTIONS</div>,
      enableSorting: false,
      cell: (info) => {
        const role = info.row.original;
        return (
          <div className="mrp-action-btn-group">
             {hasPermission('Roles', 'Edit') && (
            <button 
              type="button" 
              className="mrp-btn-action mrp-btn-edit" 
              title="Edit Role" 
              onClick={() => handleEdit(role.id)}
            >
              <FaEdit size={13} color="#ffffff" />
            </button>
             )}
              {hasPermission('Roles', 'Clone') && (
            <button 
              type="button" 
              className="mrp-btn-action mrp-btn-clone" 
              title="Clone Role" 
              onClick={() => handleClone(role)}
            >
              <FaClone size={13} color="#ffffff" />
            </button>
              )}
            {hasPermission('Roles', 'Delete') && (
            <button 
              type="button" 
              className="mrp-btn-action mrp-btn-delete" 
              title="Delete Role" 
              onClick={() => handleDelete(role)}
            >
              <FaTrash size={13} color="#ffffff" />
            </button>
            )}
          </div>
        );
      },
    }
  ], [roles]);

  // ✅ MOBILE CARD FOR DATATABLE
  const renderMobileCard = (role) => (
    <div className="role-mobile-card mb-3 p-3 bg-dark rounded shadow-sm border-start border-3 border-danger" key={role.id}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
           <h6 className="role-card-title text-white m-0 fw-bold">{role.roleName}</h6>
           <small className="text-warning fw-bold">{role.projectName}</small>
        </div>
        <span className={`mrp-status-tag ${String(role.status).toLowerCase() === 'inactive' ? 'mrp-status-inactive' : 'mrp-status-active'}`}>
          {role.status}
        </span>
      </div>
      
      <div className="role-card-body">
        <div className="info-grid text-white opacity-75 small mb-2 d-flex flex-wrap gap-2">
          <span><FaLayerGroup className="me-1 text-danger"/> {formatCamelCase(role.roleCategory)}</span>
          <span><FaBriefcase className="me-1 text-info"/> {formatCamelCase(role.auditionType)}</span>
          <span><FaMapMarkerAlt className="me-1 text-success"/> {formatCamelCase(role.roleLocation)}</span>
        </div>
        
        <div className="mt-2 d-flex justify-content-end border-top border-secondary pt-2">
            <div className="mrp-action-btn-group">
               {hasPermission('Roles', 'Edit') && (
              <button 
                type="button" 
                className="mrp-btn-action mrp-btn-edit" 
                onClick={() => handleEdit(role.id)}
              >
                <FaEdit size={13} />
              </button>
               )}
                {hasPermission('Roles', 'Clone') && (
              <button 
                type="button" 
                className="mrp-btn-action mrp-btn-clone" 
                onClick={() => handleClone(role)}
              >
                <FaClone size={13} />
              </button>
                )}
               {hasPermission('Roles', 'Delete') && (
              <button 
                type="button" 
                className="mrp-btn-action mrp-btn-delete" 
                onClick={() => handleDelete(role)}
              >
                <FaTrash size={13} />
              </button>
               )}
            </div>
        </div>
      </div>
    </div>
  );

  // ✅ LOADING VIEW
  if (loading) return (
    <div className="text-center p-5" style={{ backgroundColor: 'transparent' }}>
      <Spinner animation="border" variant="danger" />
      <p className="text-white mt-2">Loading Roles...</p>
    </div>
  );

  // ✅ MAIN RENDER
  return (
    <div className="manage-roles-page-wrapper p-2 p-md-3">
      <MetaTags title="Manage Roles" />
      
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          Manage <span style={{ color: 'var(--primary-color)' }}>Roles</span>
        </h5>
         {hasPermission('Roles', 'Create') && (
        <Button
          as={Link}
          to="/dashboard/post-roles"
          variant="danger"
          className="btn-compact-header fw-bold"
        >
          <FaPlus className="me-1" /> Post Role
        </Button>
         )}
      </div>

      {error && <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">{error}</Alert>}

      <div className="roles-kpi-grid">
        {[
          { title: "Total Projects", value: dashboardStats.totalProjects, icon: <FaFilm />, trend: "Projects" },
          { title: "Active Roles", value: dashboardStats.totalRoles, icon: <FaLayerGroup />, trend: "Active" },
          { title: "Total Applicants", value: dashboardStats.totalAuditions, icon: <FaUsers />, trend: "Applicants" },
          { title: "Shortlisted", value: dashboardStats.selected, icon: <FaCheckCircle />, trend: "Selected" },
          { title: "Rejected", value: dashboardStats.rejected, icon: <FaTimesCircle />, trend: "Rejected" },
          { title: "Pending", value: dashboardStats.pending, icon: <FaClock />, trend: "Waiting" }
        ].map((item, index) => (
          <div className="roles-kpi-card" key={index}>
            <div className="roles-kpi-top">
              <div className="roles-kpi-icon">{item.icon}</div>
              <div className="roles-kpi-trend">{item.trend}</div>
            </div>
            <div className="roles-kpi-bottom">
              <p className="roles-kpi-label">{item.title}</p>
              <h3 className="roles-kpi-value">{item.value}</h3>
            </div>
          </div>
        ))}
      </div>

      <DataTable 
        columns={columns} 
        data={roles} 
        renderMobileItem={renderMobileCard} 
        searchPlaceholder="Search roles or projects..." 
      />
    </div>
  );
};

export default ManageRolesPage;